import type { IPageState } from "@rhc-shared-components/table-toolbar";
import { query } from "gql-query-builder";
import { ProductKeys, ProductVersionKeys } from "../constants/product";
import { ProjectKeys } from "../constants/project";
import type {
	IEnumFieldMetadataGraphQL,
	IEnumVendorMetadataGraphQL,
	IGraphQLMainError,
	IProductListGraphQL,
	IProductVersionsEntity,
	IProjectListGraphQLResponse,
	ProjectListGraphQLResponseData,
} from "../interfaces/graphql";
import type { IPaginationRequest } from "../modal/pagination";
import type {
	CertificationProject,
	CertificationProjectFilter,
	ProductListing,
	ProductListingFilter,
	SortBy,
} from "../types/generated-graphql";
import ENV from "../utils/env";
import { addQueryParamsToUri, deleteUri, getOrgID, getUri, patchUri, postUri } from "../utils/fetch";

interface IGetProjectsListParams {
	sort_by?: string[];
	container_type?: string;
	filter?: string[];
}

export function getProjectsList(
	orgId: string,
	pagination: IPaginationRequest,
	extraParams: IGetProjectsListParams = {},
	abortSignal?: AbortSignal,
): Promise<unknown> {
	const uri = ENV.apiHostName
		.clone()
		.setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/vendors/org-id/${orgId}/projects/certification`);
	addQueryParamsToUri(uri, pagination);
	addQueryParamsToUri(uri, extraParams);
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		// this will be coming from useFetch hook
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return getUri(uri, additionalParams);
}

export function getProjectListQuery(
	paginationObj: IPageState = {
		page: 1,
		perPage: 10,
	},
	filerObject: CertificationProjectFilter,
	sortObject: SortBy[],
	abortSignal?: AbortSignal,
): Promise<IProjectListGraphQLResponse> {
	const orgId = Number(getOrgID());
	const imageListQuery = query({
		operation: {
			name: "find_vendor_certification_projects_by_org_id",
			alias: "projectList",
		},
		variables: {
			orgId: { name: "org_id", value: orgId, required: true, type: "Int" },
			sortBy: { name: "sort_by", value: sortObject, required: true, type: "[SortBy]" },
			filter: { name: "filter", value: filerObject, required: true, type: "CertificationProjectFilter" },
			pageNumber: { name: "page", value: paginationObj.page - 1, required: true, type: "Int" },
			pageSize: { name: "page_size", value: paginationObj.perPage, required: true, type: "Int" },
		},
		fields: [
			{
				error: ["status", "detail"],
			},
			{
				data: [
					"_id",
					"name",
					"type",
					{
						container: ["type"],
					},
					"last_update_date",
					"certification_status",
					"certification_level",
					"product_listings",
					"project_status",
					"catalog_visibility",
				],
			},
			"total",
		],
	});
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		// this will be coming from useFetch hook
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(ENV.pyxisGraphqlAPIURL, imageListQuery, additionalParams);
}

interface ProductNameListGraphQLResponse {
	data: {
		productList: {
			data: Pick<ProductListing, "_id" | "name">[];
		};
	};
}

export function getProductNameByProductIdsQuery(
	productIds: string[],
	abortSignal?: AbortSignal,
): Promise<ProductNameListGraphQLResponse> {
	const orgId = Number(getOrgID());
	const productNameQuery = query({
		operation: {
			name: "find_vendor_product_listings_by_org_id",
			alias: "productList",
		},
		variables: {
			orgId: { name: "org_id", value: orgId, required: true, type: "Int" },
			filter: {
				name: "filter",
				value: {
					_id: {
						in: productIds,
					},
				},
				required: true,
				type: "ProductListingFilter",
			},
			pageNumber: { name: "page", value: 0, required: true, type: "Int" },
			pageSize: { name: "page_size", value: 250, required: true, type: "Int" },
		},
		fields: [
			{
				error: ["status", "detail"],
			},
			{
				data: ["_id", "name"],
			},
		],
	});
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		// this will be coming from useFetch hook
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(ENV.pyxisGraphqlAPIURL, productNameQuery, additionalParams);
}

// PATCHes (Partially Updates) a Project
export function patchCertificationProject(projectData: Partial<CertificationProject>): Promise<CertificationProject> {
	const uri = ENV.apiHostName
		.clone()
		.setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/projects/certification/id/${projectData[ProjectKeys.id]}`);
	return patchUri(uri, projectData);
}

// GETs all the Products Listings
export function getProductListingsQuery(
	orgID: number,
	pagination: IPaginationRequest,
	filter?: ProductListingFilter,
	sortBy?: SortBy[],
	abortSignal?: AbortSignal,
): Promise<ProductListGraphQLResponse> {
	const orgId = Number(orgID);
	const productListingsQuery = query([
		{
			operation: {
				name: "find_vendor_product_listings_by_org_id",
				alias: "productListings",
			},
			variables: {
				orgID: { name: "org_id", value: orgId, required: true, type: "Int" },
				productListingsFilter: {
					name: "filter",
					value: filter,
					required: false,
					type: "ProductListingFilter",
				},
				productListingsSortBy: {
					name: "sort_by",
					value: sortBy || [{ field: "last_update_date", order: "DESC" }],
					required: false,
					type: "[SortBy]",
				},
				productListingsPage: { name: "page", value: pagination.page, required: true, type: "Int" },
				productListingsPageSize: { name: "page_size", value: pagination.page_size, required: true, type: "Int" },
			},
			fields: [
				{
					error: ["status", "detail"],
				},
				{
					data: [
						"_id",
						"name",
						"type",
						"last_update_date",
						"published",
						"cert_projects",
						"target_platforms",
						{
							logo: ["url"],
						},
						{
							descriptions: ["long", "short"],
						},
						{
							contacts: ["email_address", "type"],
						},
						{
							features: ["description", "title"],
						},
						{
							quick_start_configuration: ["instructions"],
						},
						{
							linked_resources: ["type", "title", "description", "url"],
						},
						{
							faqs: ["answer", "question"],
						},
						{
							support: ["description", "email_address", "phone_number", "url"],
						},
						{
							search_aliases: ["key", "value"],
						},
						"functional_categories",
						{
							product_validation: ["partner_validated", "workload_type", "validation_date"],
						},
					],
				},
				"total",
			],
		},
	]);
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(ENV.pyxisGraphqlAPIURL, productListingsQuery, additionalParams);
}

interface ProductListGraphQLResponse {
	data: {
		productListings: {
			data: ProductListing[];
			total: number;
		};
	};
}
export function findProductListingsByNameQuery(
	orgID: number,
	pagination: IPaginationRequest,
	filter?: ProductListingFilter,
	searchValue?: string,
	sortBy?: SortBy[],
	abortSignal?: AbortSignal,
): Promise<ProductListGraphQLResponse> {
	const orgId = Number(orgID);
	const productListingsQuery = query([
		{
			operation: {
				name: "find_product_listings_by_name_org_id",
				alias: "productListings",
			},
			variables: {
				orgID: { name: "org_id", value: orgId, required: true, type: "Int" },
				searchValue: { name: "name", value: searchValue },
				productListingsFilter: {
					name: "filter",
					value: filter,
					required: false,
					type: "ProductListingFilter",
				},
				productListingsSortBy: {
					name: "sort_by",
					value: sortBy || [{ field: "last_update_date", order: "DESC" }],
					required: false,
					type: "[SortBy]",
				},
				productListingsPage: { name: "page", value: pagination.page, required: true, type: "Int" },
				productListingsPageSize: { name: "page_size", value: pagination.page_size, required: true, type: "Int" },
			},
			fields: [
				{
					error: ["status", "detail"],
				},
				{
					data: [
						"_id",
						"name",
						"type",
						"last_update_date",
						"published",
						"cert_projects",
						"target_platforms",
						{
							logo: ["url"],
						},
						{
							descriptions: ["long", "short"],
						},
					],
				},
				"total",
			],
		},
	]);
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(ENV.pyxisGraphqlAPIURL, productListingsQuery, additionalParams);
}

// GETs a Product Listing details by ID + Functional categories metadata
export function getProductListingByIdAndMetadataGraphQL(
	id: string,
	isMetadataLoaded: boolean,
	isVendorMetadataLoaded: boolean,
	abortSignal?: AbortSignal,
): Promise<{
	data: {
		get_product_listing: IProductListGraphQL;
		functional_categories_metadata: IEnumFieldMetadataGraphQL;
		vendor_metadata: IEnumVendorMetadataGraphQL;
	};
	errors?: IGraphQLMainError[];
}> {
	const productNameQuery = query([
		{
			operation: {
				name: "get_product_listing",
				alias: "get_product_listing",
			},
			variables: {
				id: { name: "id", value: id, required: true, type: "ObjectIDFilterScalar" },
			},
			fields: [
				{
					error: ["status", "detail"],
				},
				{
					data: [
						"_id",
						"category",
						"cert_projects",
						"org_id",
						{
							contacts: ["type", "email_address"],
						},
						{
							descriptions: ["long", "short"],
						},
						{
							faqs: ["answer", "question"],
						},
						{
							features: ["description", "title"],
						},
						"functional_categories",
						{
							legal: ["description", "license_agreement_url", "privacy_policy_url"],
						},
						{
							linked_resources: ["description", "title", "type", "url"],
						},
						{
							logo: ["url"],
						},
						"name",
						"published",
						"deleted",
						{
							quick_start_configuration: ["instructions", "default_deploy_instructions"],
						},
						"repositories",
						{
							search_aliases: ["key", "value"],
						},
						{
							support: ["description", "email_address", "phone_number", "url"],
						},
						"type",
						"certification_badges_all",
						"certification_badges",
						"certification_level",
						"deployed_as_all",
						"deployed_as",
						"creation_date",
						"last_update_date",
						"target_platforms",
						{
							product_validation: ["partner_validated", "workload_type", "validation_date"],
						},
						{
							edges: [
								{
									operation: "cert_projects",
									variables: {
										componentSortBy: {
											name: "sort_by",
											value: { field: "last_update_date", order: "DESC" },
											required: false,
											type: "[SortBy]",
										},
										certProjectsPageSize: { name: "page_size", value: 100, required: true, type: "Int" },
									},
									fields: [
										{
											data: [
												"_id",
												"name",
												"last_update_date",
												"creation_date",
												"badges",
												"certification_level",
												"certification_status",
												"type",
												"project_status",
												"product_listings",
												"catalog_visibility",
												{
													redhat: ["product_id", "product_name", "product_version", "product_version_id"],
												},
												{
													container: ["type"],
												},
												{
													edges: [
														{
															operation: "images",
															variables: {
																imageListPageNumber: { name: "page", value: 0, required: true, type: "Int" },
																imageListPageSize: { name: "page_size", value: 1, required: true, type: "Int" },
																imageListSortBy: {
																	name: "sort_by",
																	value: {
																		field: "creation_date",
																		order: "DESC",
																	},
																	required: true,
																	type: "[SortBy]",
																},
																imageListFilter: {
																	name: "filter",
																	value: {
																		and: [{ deleted: { ne: true } }],
																	},
																	required: true,
																	type: "ContainerImageFilter",
																},
															},
															fields: [
																{
																	data: [
																		"_id",
																		{
																			freshness_grades: ["grade", "start_date", "end_date"],
																		},
																		{
																			edges: [
																				{
																					vulnerabilities: ["total"],
																				},
																				{
																					operation: "test_results",
																					variables: {
																						testResultPageNumber: {
																							name: "page",
																							value: 0,
																							required: true,
																							type: "Int",
																						},
																						testResultPageSize: {
																							name: "page_size",
																							value: 1,
																							required: true,
																							type: "Int",
																						},
																						testResultSortBy: {
																							name: "sort_by",
																							value: {
																								field: "creation_date",
																								order: "DESC",
																							},
																							required: true,
																							type: "[SortBy]",
																						},
																					},
																					fields: [
																						{
																							data: [
																								"_id",
																								{
																									results: [
																										{
																											failed: ["name"],
																										},
																									],
																								},
																							],
																						},
																					],
																				},
																			],
																		},
																	],
																},
															],
														},
													],
												},
											],
										},
										"total",
									],
								},
								{
									product_validations: [
										{
											data: [
												"_id",
												"partner_validated",
												{
													redhat_affinity: [
														"id",
														"name",
														"support_future_versions",
														"short_name",
														{
															versions: ["id", "name"],
														},
													],
												},
											],
										},
									],
								},
								{
									operation: "product_versions",
									variables: {
										id: { name: "id", value: id, required: true, type: "ObjectIDFilterScalar" },
										pageNumber: { name: "page", value: 0, required: true, type: "Int" },
										pageSize: { name: "page_size", value: 250, required: true, type: "Int" },
										sortBy: {
											name: "sort_by",
											value: { field: "creation_date", order: "DESC" },
											required: false,
											type: "[SortBy]",
										},
									},
									fields: [
										{
											data: [
												"product_version",
												"product_listing",
												"_id",
												"creation_date",
												"last_update_date",
												{
													redhat_affinity: ["versions", "product_name", "support_future_versions"],
												},
											],
										},
									],
								},
							],
						},
					],
				},
			],
		},
		...(isMetadataLoaded
			? []
			: [
					{
						operation: {
							name: "get_field_metadata",
							alias: "functional_categories_metadata",
						},
						variables: {
							typeName: { name: "type_name", value: "ProductListing", required: true, type: "String" },
							fieldName: { name: "field_name", value: "functional_categories", required: true, type: "String" },
						},
						fields: [
							{
								error: ["status", "detail"],
							},
							{
								data: ["enum_values"],
							},
						],
					},
				]),
		...(isVendorMetadataLoaded
			? []
			: [
					{
						operation: {
							name: "get_vendor_by_org_id",
							alias: "vendor_metadata",
						},
						variables: {
							orgId: { name: "org_id", value: Number(getOrgID()), required: true, type: "Int" },
						},
						fields: [
							{
								error: ["status", "detail"],
							},
							{
								data: ["allow_hosted_container_registry"],
							},
						],
					},
				]),
	]);

	const additionalParams: RequestInit = {};
	if (abortSignal) {
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(ENV.pyxisGraphqlAPIURL, productNameQuery, additionalParams);
}

export function getProductAttachedComponents(
	id: string,
	pagination: IPageState,
	abortSignal?: AbortSignal,
): Promise<{
	data: {
		AttachedComponentsToProductsList: ProjectListGraphQLResponseData;
	};
}> {
	const getAttachedComponentsList = query([
		{
			operation: {
				name: "find_product_listing_certification_projects",
				alias: "AttachedComponentsToProductsList",
			},
			variables: {
				id: { name: "id", value: id, required: true, type: "ObjectIDFilterScalar" },
				componentSortBy: {
					name: "sort_by",
					value: { field: "last_update_date", order: "DESC" },
					required: false,
					type: "[SortBy]",
				},
				certProjectsPage: { name: "page", value: pagination?.page, required: true, type: "Int" },
				certProjectsPageSize: { name: "page_size", value: pagination?.perPage, required: true, type: "Int" },
			},
			fields: [
				{
					error: ["status", "detail"],
				},
				{
					data: [
						"_id",
						"name",
						"last_update_date",
						"creation_date",
						"badges",
						"certification_level",
						"certification_status",
						"type",
						"project_status",
						"product_listings",
						{
							redhat: ["product_id", "product_name", "product_version", "product_version_id"],
						},
						{
							container: ["type"],
						},
						{
							edges: [
								{
									operation: "images",
									variables: {
										imageListPageNumber: { name: "page", value: 0, required: true, type: "Int" },
										imageListPageSize: { name: "page_size", value: 1, required: true, type: "Int" },
										imageListSortBy: {
											name: "sort_by",
											value: {
												field: "creation_date",
												order: "DESC",
											},
											required: true,
											type: "[SortBy]",
										},
										imageListFilter: {
											name: "filter",
											value: {
												and: [{ deleted: { ne: true } }],
											},
											required: true,
											type: "ContainerImageFilter",
										},
									},
									fields: [
										{
											data: [
												"_id",
												{
													freshness_grades: ["grade", "start_date", "end_date"],
												},
												{
													edges: [
														{
															vulnerabilities: ["total"],
														},
													],
												},
											],
										},
									],
								},
							],
						},
					],
				},
				"total",
			],
		},
	]);
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(ENV.pyxisGraphqlAPIURL, getAttachedComponentsList, additionalParams);
}

// POSTs a Product Listing
export function createProductListing(
	productListingData: ProductListing,
	abortSignal?: AbortSignal,
): Promise<ProductListing> {
	const uri = ENV.apiHostName.clone().setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/product-listings`);

	const additionalParams: RequestInit = {};
	if (abortSignal) {
		// this will be coming from useFetch hook
		Object.assign(additionalParams, { signal: abortSignal });
	}

	return postUri(uri, productListingData, additionalParams);
}

export function patchProductListing(productListingData: Partial<ProductListing>): Promise<ProductListing> {
	const uri = ENV.apiHostName
		.clone()
		.setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/product-listings/id/${productListingData[ProductKeys.id]}`);
	return patchUri(uri, productListingData);
}

// POSTs a project
export const createProject = (
	projectData: Record<string, unknown>,
	abortSignal?: AbortSignal,
): Promise<CertificationProject> => {
	const uri = ENV.apiHostName.clone().setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/projects/certification`);
	const params = abortSignal ? { signal: abortSignal } : {};
	return postUri(uri, projectData, params);
};

// Product versions APIs
export function deleteProductVersion(productVersionId: string): Promise<string> {
	const uri = ENV.apiHostName
		.clone()
		.setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/product-versions/id/${productVersionId}`);
	return deleteUri(uri);
}

export function createProductVersion(
	productVersionData: Partial<IProductVersionsEntity>,
): Promise<IProductVersionsEntity> {
	const uri = ENV.apiHostName.clone().setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/product-versions`);

	const additionalParams: RequestInit = {};
	return postUri(uri, productVersionData, additionalParams);
}

export function updateProductVersions(
	productVersionData: Partial<IProductVersionsEntity>,
): Promise<IProductVersionsEntity> {
	const uri = ENV.apiHostName
		.clone()
		.setPath(`${ENV.apiPrefix}/${ENV.apiVersion}/product-versions/id/${productVersionData[ProductVersionKeys.id]}`);

	const additionalParams: RequestInit = {};
	return patchUri(uri, productVersionData, additionalParams);
}
